import React, { useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Upload from '@shared/molecules/Upload';

import { contentType, courseStatus, PROCESSING_STATUS } from '@shared/const';
import theme from '@shared/themes/default';
import { useAppGlobals } from '../../../../../contexts/AppContext';
import { useCourse } from '../../../../../contexts/CourseContext';

import {
  validateCourseFiles,
  parseCourseFiles,
} from '../../../parseCourseFiles';

const SectionStyled = styled.section`
  background: ${theme.palette.darkWrapper};
  margin: ${theme.spacing.s};
  padding: ${theme.spacing.m1};
  width: 506px;
  height: 100%;
`;

const DrawerBatch = ({ onChange, setChangedDrawerContent, setError }) => {
  const { t } = useTranslation();
  const { course, hasChanged, setHasChanged, setUploadingFiles } = useCourse();
  const { getRestClient, locale } = useAppGlobals();
  const [items, setItems] = useState([]);
  const isPublished = course.status === courseStatus.PUBLISHED;
  const { episodes } = course;

  const processFiles = (files) => {
    try {
      const nameFiles = files.map((file) => file.name);
      const { episodes } = parseCourseFiles(course, nameFiles, isPublished);

      const newItems = {};
      Object.keys(episodes).forEach((key) => {
        const { name, file, subtitles, trailer } = episodes[key];
        if (trailer) {
          const selectedFile = files.find((f) => f.name === file);
          const trailerItem = { id: 'trailer', duration: 0 };
          let includeTrailer = false;

          if (selectedFile) {
            trailerItem.file = {
              file: selectedFile.s3File,
              processingStatus: PROCESSING_STATUS.TO_SCHEDULE,
            };
            trailerItem.videoFiles = [];
            includeTrailer = true;
          }

          if (subtitles && typeof subtitles === 'object') {
            const newSubtitles = [...(course.trailer?.subtitles || [])];
            let hasSubtitleError = false;

            Object.entries(subtitles).forEach(([langCode, subtitleData]) => {
              const isErrorSubtitle =
                typeof subtitleData === 'object' && subtitleData.hasError;
              const subtitleFilename = isErrorSubtitle
                ? subtitleData.file
                : subtitleData;

              if (isErrorSubtitle) {
                hasSubtitleError = true;
              }

              const subtitleFile = files.find(
                (f) => f.name === subtitleFilename
              );
              if (subtitleFile) {
                const existingIndex = newSubtitles.findIndex(
                  (s) => s.language.code === langCode
                );
                if (existingIndex >= 0) {
                  newSubtitles.splice(existingIndex, 1);
                }

                newSubtitles.push({
                  file: {
                    file: subtitleFile.s3File,
                    processingStatus: PROCESSING_STATUS.TO_SCHEDULE,
                  },
                  fileName: subtitleFilename,
                  language: {
                    code: langCode,
                  },
                  hasError: isErrorSubtitle,
                });
              }
            });

            if (hasSubtitleError) {
              setError(t('backoffice.courses.lessonsTab.missingLanguageCode'));
            }

            if (newSubtitles.length > 0) {
              trailerItem.subtitles = newSubtitles;
              includeTrailer = true;
            }
          }

          if (includeTrailer) {
            newItems[0] = trailerItem;
          }
        } else {
          const originalEpisode = course.episodes[key - 1];
          let newItem = {
            contentType: contentType.VIDEO,
            ...(!isPublished && {
              name: [
                {
                  languageCode: locale,
                  value: name,
                },
              ],
            }),
            ordinal: Number(key),
          };
          const selectedFile = files.find((f) => f.name === file);
          if (selectedFile) {
            newItem = {
              ...newItem,
              content: {
                ...(originalEpisode?.content || {}),
                duration: 0,
                file: {
                  file: selectedFile.s3File,
                  processingStatus: PROCESSING_STATUS.TO_SCHEDULE,
                },
                videoFiles: [],
              },
            };
          }
          if (subtitles && typeof subtitles === 'object') {
            const newSubtitles = [
              ...(originalEpisode?.content?.subtitles || []),
            ];
            let hasSubtitleError = false;

            Object.entries(subtitles).forEach(([langCode, subtitleData]) => {
              const isErrorSubtitle =
                typeof subtitleData === 'object' && subtitleData.hasError;
              const subtitleFilename = isErrorSubtitle
                ? subtitleData.file
                : subtitleData;

              if (isErrorSubtitle) {
                hasSubtitleError = true;
              }

              const subtitleFile = files.find(
                (f) => f.name === subtitleFilename
              );
              if (subtitleFile) {
                const existingIndex = newSubtitles.findIndex(
                  (s) => s.language.code === langCode
                );
                if (existingIndex >= 0) {
                  newSubtitles.splice(existingIndex, 1);
                }

                newSubtitles.push({
                  file: {
                    file: subtitleFile.s3File,
                    processingStatus: PROCESSING_STATUS.TO_SCHEDULE,
                  },
                  fileName: subtitleFilename,
                  language: {
                    code: langCode,
                  },
                  hasError: isErrorSubtitle,
                });
              }
            });

            if (hasSubtitleError) {
              setError(t('backoffice.courses.lessonsTab.missingLanguageCode'));
            }

            if (newSubtitles.length > 0) {
              newItem = {
                ...newItem,
                content: {
                  ...(originalEpisode?.content || {}),
                  ...(newItem.content || {}),
                  subtitles: newSubtitles,
                },
              };
            }
          }
          newItems[key] = newItem;
        }
      });
      return newItems;
    } catch (e) {
      setError(e.message);
    }
    return [];
  };

  const handleUpload = (files) => {
    if (files?.length > 0) {
      const totalFiles = [...items, ...files];
      const newItems = processFiles(totalFiles);
      setItems(totalFiles);
      if (onChange) onChange(newItems);
    }
  };

  const handleRemoveFile = (filenameToRemove) => {
    const totalFiles = items.filter((file) => file.name !== filenameToRemove);
    const newItems = processFiles(totalFiles);
    setItems(totalFiles);
    if (onChange) onChange(newItems);
  };

  const onStartUpload = () => {
    if (!hasChanged) setHasChanged(true);
    setChangedDrawerContent(true);
    setUploadingFiles(true);
  };

  return (
    <SectionStyled>
      <Upload
        chunkSize={1024 * 1024 * 100}
        description={
          isPublished
            ? [
                t('backoffice.courses.lessonsTab.batchUploadDescriptionHeader'),
                t('backoffice.courses.lessonsTab.subtitlesBatchUpload'),
              ]
            : [
                t('backoffice.courses.lessonsTab.batchUploadDescriptionHeader'),
                t(
                  'backoffice.courses.lessonsTab.batchUploadDescriptionLessons'
                ),
                t(
                  'backoffice.courses.lessonsTab.batchUploadDescriptionSubtitle'
                ),
                t('backoffice.courses.lessonsTab.or'),
                t(
                  'backoffice.courses.lessonsTab.batchUploadDescriptionSubtitle2'
                ),
              ]
        }
        onCompleteUpload={handleUpload}
        onDeleteFile={handleRemoveFile}
        onStartUpload={onStartUpload}
        onUploadSettled={() => setUploadingFiles(false)}
        restClient={getRestClient()}
        validate={(files) => {
          const { validatedFiles, errors } = validateCourseFiles(
            course,
            files,
            isPublished,
            episodes.length
          );
          setError(
            errors.length === 0
              ? null
              : t('backoffice.courses.lessonsTab.batchUploadError', {
                  count: errors.length,
                })
          );
          return validatedFiles;
        }}
      />
    </SectionStyled>
  );
};

DrawerBatch.propTypes = {
  onChange: PropTypes.func,
  setChangedDrawerContent: PropTypes.func.isRequired,
  setError: PropTypes.func,
};

export default DrawerBatch;
